import { createApi } from '@reduxjs/toolkit/query/react'
import { MIN_PERSISTENCE_CLIENT_VERSION } from 'src/constant'
import {
  Apis,
  ConfigOptions,
  ConfigResponse,
  GetMeetingContextResponse,
  GetMeetingParticipantsResponse,
  GetUserContextResponse,
  GetAppContextResponse,
} from 'src/types/sdk'

const zoomSdkBaseQuery = async ({
  method,
  params,
}: {
  method: Apis | 'config'
  params?: any
}) => {
  try {
    const result = await window.zoomSdk[method](params)
    return { data: result }
  } catch (err) {
    return {
      error: {
        name: err?.status || err?.name,
        message: err?.data || err.message,
      },
    }
  }
}

export const configParams: ConfigOptions = {
  version: '0.16',
  capabilities: [
    'getMeetingContext',
    'getMeetingParticipants',
    'getRunningContext',
    'getUserContext',
    'launchAppInMeeting',
    'onMyMediaChange',
    'onMyUserContextChange',
    'onParticipantChange',
    'onRunningContextChange',
    'openUrl',
    'removeVirtualForeground',
    'sendAppInvitation',
    'setVirtualForeground',
    'shareApp',
    'getAppContext',
  ],
}

export const zoomSdkApi = createApi({
  reducerPath: 'zoomSdk',
  baseQuery: zoomSdkBaseQuery,
  endpoints: (builder) => ({
    config: builder.query<
      ConfigResponse & { hasVFPersistence: boolean },
      'config'
    >({
      query: () => ({ method: 'config', params: configParams }),
      transformResponse: (response: ConfigResponse) => ({
        ...response,
        hasVFPersistence: !!(
          response.clientVersion &&
          parseInt(response.clientVersion.split('.').join('')) >=
            MIN_PERSISTENCE_CLIENT_VERSION
        ),
      }),
    }),
    // to obtain the meeting name and id for the session
    getMeetingContext: builder.query<
      GetMeetingContextResponse,
      'getMeetingContext'
    >({
      query: () => ({ method: 'getMeetingContext' }),
    }),
    getAppContext: builder.query<GetAppContextResponse, 'getAppContext'>({
      query: () => ({ method: 'getAppContext' }),
    }),
    // to obtain the user role for view
    getUserContext: builder.query<GetUserContextResponse, 'getUserContext'>({
      query: () => ({ method: 'getUserContext' }),
    }),
    // only used once, then event listener hook manages the data after
    getMeetingParticipants: builder.query<
      GetMeetingParticipantsResponse,
      'getMeetingParticipants'
    >({
      query: () => ({ method: 'getMeetingParticipants' }),
      transformResponse: (response: GetMeetingParticipantsResponse) => ({
        // remove the host from the list
        participants: response.participants.filter(
          (participant) => participant.role !== 'host'
        ),
      }),
    }),
  }),
})

export const {
  useConfigQuery,
  useGetUserContextQuery,
  useGetMeetingContextQuery,
  useGetMeetingParticipantsQuery,
  useGetAppContextQuery,
} = zoomSdkApi
